/**
 * Chat panel — primitive surface. Renders QT bubbles, system blocks,
 * and pilot echoes; reports submits via onPilotSubmit. Flow control
 * lives in the per-scene harnesses.
 */

const QT_MS_PER_CHAR      = 22;   // ~45 chars/sec
const SYSTEM_MS_PER_LINE  = 180;
const PAYSTUB_MS_PER_LINE = 120;  // dense table — slower line cadence
const FAST_SYSTEM_MS_PER_LINE = 60; // data-flood blocks
const INTER_MESSAGE_PAUSE = 500;  // breath between QT/Pilot/System messages
const SYSTEM_BLOCK_PAUSE  = 700;  // longer breath before/after SYSTEM blocks

export interface SystemBlockSpec {
  header: string;
  body?: string[];
  buttons?: { label: string; onClick: () => void }[];
  fast?: boolean;
}

export interface ChatPanelApi {
  appendQT(text: string, opts?: { typewrite?: boolean }): Promise<void>;
  appendSystem(block: SystemBlockSpec): { collapse(): void };
  appendPilot(text: string): void;
  onPilotSubmit(handler: (text: string) => void): void;
  setInputEnabled(enabled: boolean): void;
  setSpeakerLabel(label: string): void;
  setGrayedOut(grayed: boolean): void;
  blurInput(): void;
  focusInput(): void;
}

export function initChatPanel(): ChatPanelApi {
  const root = document.getElementById("chat-panel");
  if (!root) throw new Error("#chat-panel not found in DOM");

  root.innerHTML = "";
  root.style.display = "flex";
  root.style.flexDirection = "column";

  let speakerLabel = "QT-3.14";

  // ─── Log ─────────────────────────────────────────────────────────
  const log = document.createElement("div");
  Object.assign(log.style, {
    flex: "1 1 auto",
    overflowY: "auto",
    paddingRight: "6px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    fontSize: "12px",
    lineHeight: "1.4",
    fontFamily: "\"Nunito\", \"Avenir Next\", system-ui, sans-serif",
    minHeight: "0",
    color: "var(--cubicle-text)",
  } as CSSStyleDeclaration);

  // ─── Input row ───────────────────────────────────────────────────
  const inputRow = document.createElement("form");
  Object.assign(inputRow.style, {
    flex: "0 0 auto",
    marginTop: "4px",
    display: "flex",
    gap: "8px",
    alignItems: "stretch",
  } as CSSStyleDeclaration);

  const input = document.createElement("input");
  input.type = "text";
  input.placeholder = "▸ Say something…";
  input.autocomplete = "off";
  Object.assign(input.style, {
    flex: "1 1 auto",
    background: "var(--cubicle-bezel)",
    border: "1px solid var(--cubicle-bezel-edge)",
    color: "var(--cubicle-text)",
    padding: "6px 10px",
    fontFamily: "\"Nunito\", \"Avenir Next\", system-ui, sans-serif",
    fontSize: "13px",
    outline: "none",
    borderRadius: "3px",
    letterSpacing: "0",
  } as CSSStyleDeclaration);
  input.addEventListener("focus", () => {
    input.style.borderColor = "var(--cubicle-amber)";
    input.style.boxShadow = "0 0 0 1px color-mix(in srgb, var(--cubicle-amber) 30%, transparent)";
  });
  input.addEventListener("blur", () => {
    input.style.borderColor = "var(--cubicle-bezel-edge)";
    input.style.boxShadow = "none";
  });

  const sendBtn = document.createElement("button");
  sendBtn.type = "submit";
  sendBtn.textContent = "SEND";
  Object.assign(sendBtn.style, {
    background: "color-mix(in srgb, var(--cubicle-amber) 8%, transparent)",
    border: "1px solid var(--cubicle-bezel-edge)",
    color: "var(--cubicle-amber)",
    padding: "0 10px",
    fontFamily: "\"Nunito\", \"Avenir Next\", system-ui, sans-serif",
    fontSize: "11px",
    letterSpacing: "0.10em",
    cursor: "pointer",
    borderRadius: "3px",
    flex: "0 0 auto",
  } as CSSStyleDeclaration);
  sendBtn.addEventListener("mouseenter", () => {
    sendBtn.style.background = "color-mix(in srgb, var(--cubicle-amber) 16%, transparent)";
    sendBtn.style.borderColor = "var(--cubicle-amber)";
  });
  sendBtn.addEventListener("mouseleave", () => {
    sendBtn.style.background = "color-mix(in srgb, var(--cubicle-amber) 8%, transparent)";
    sendBtn.style.borderColor = "var(--cubicle-bezel-edge)";
  });

  inputRow.append(input, sendBtn);
  root.append(log, inputRow);

  // ─── Render helpers ──────────────────────────────────────────────
  function appendQTRow(): HTMLElement {
    const row = document.createElement("div");
    const label = document.createElement("span");
    label.textContent = speakerLabel + ": ";
    Object.assign(label.style, {
      color: "var(--cubicle-amber)",
      fontSize: "11px",
      fontFamily: "ui-monospace, \"SF Mono\", Menlo, Consolas, monospace",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      fontWeight: "700",
      userSelect: "none",
    } as CSSStyleDeclaration);

    const body = document.createElement("span");
    Object.assign(body.style, {
      color: "var(--cubicle-text)",
      fontSize: "12px",
      fontFamily: "\"Nunito\", \"Avenir Next\", system-ui, sans-serif",
    } as CSSStyleDeclaration);

    row.append(label, body);
    log.append(row);
    log.scrollTop = log.scrollHeight;
    return body;
  }

  function appendPilotRow(text: string): void {
    const row = document.createElement("div");
    const label = document.createElement("span");
    label.textContent = "OPERATOR: ";
    Object.assign(label.style, {
      color: "var(--cubicle-blue)",
      fontSize: "11px",
      fontFamily: "ui-monospace, \"SF Mono\", Menlo, Consolas, monospace",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      fontWeight: "700",
      opacity: "0.6",
      userSelect: "none",
    } as CSSStyleDeclaration);

    const body = document.createElement("span");
    body.textContent = text;
    Object.assign(body.style, {
      color: "var(--cubicle-text)",
      fontSize: "12px",
      opacity: "0.85",
      fontFamily: "\"Nunito\", \"Avenir Next\", system-ui, sans-serif",
    } as CSSStyleDeclaration);

    row.append(label, body);
    log.append(row);
    log.scrollTop = log.scrollHeight;
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  async function typewriteEl(el: HTMLElement, text: string): Promise<void> {
    const chars = [...text];
    el.textContent = "";
    for (const ch of chars) {
      el.textContent += ch;
      log.scrollTop = log.scrollHeight;
      await sleep(QT_MS_PER_CHAR);
    }
  }

  // ─── Render queue ─────────────────────────────────────────────────
  interface Job {
    kind: "qt" | "system" | "pilot";
    cancelled: boolean;
    run(): Promise<void>;
  }

  const queue: Job[] = [];
  let draining = false;

  async function drain(): Promise<void> {
    if (draining) return;
    draining = true;
    let prevKind: Job["kind"] | null = null;
    while (queue.length > 0) {
      const job = queue.shift()!;
      if (job.cancelled) continue;
      if (prevKind !== null) {
        const pause = (prevKind === "system" || job.kind === "system")
          ? SYSTEM_BLOCK_PAUSE
          : INTER_MESSAGE_PAUSE;
        await sleep(pause);
      }
      await job.run();
      prevKind = job.kind;
    }
    draining = false;
  }

  function enqueue(job: Job): void {
    queue.push(job);
    void drain();
  }

  // ─── Public API ───────────────────────────────────────────────────
  function appendQT(text: string, opts?: { typewrite?: boolean }): Promise<void> {
    return new Promise<void>((resolve) => {
      const job: Job = {
        kind: "qt",
        cancelled: false,
        async run() {
          const el = appendQTRow();
          if (opts?.typewrite === false) {
            el.textContent = text;
          } else {
            await typewriteEl(el, text);
          }
          resolve();
        },
      };
      enqueue(job);
    });
  }

  function appendSystem(block: SystemBlockSpec): { collapse(): void } {
    let renderedEl: HTMLElement | null = null;
    const job: Job = {
      kind: "system",
      cancelled: false,
      async run() {
        const wrap = document.createElement("div");
        Object.assign(wrap.style, {
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          border: "1px solid var(--cubicle-bezel-edge)",
          padding: "10px 14px",
          background: "var(--cubicle-bezel)",
          fontFamily: "ui-monospace, \"SF Mono\", Menlo, Consolas, monospace",
        } as CSSStyleDeclaration);

        const header = document.createElement("div");
        Object.assign(header.style, {
          fontSize: "10px",
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          fontWeight: "700",
          color: "var(--cubicle-amber)",
          opacity: "0.85",
        } as CSSStyleDeclaration);
        header.textContent = `◂ ${block.header} ▸`;
        wrap.append(header);
        log.append(wrap);
        log.scrollTop = log.scrollHeight;

        if (block.body && block.body.length > 0) {
          const isPaystub = block.header.startsWith("SHIFT RESULT");
          const msPerLine = block.fast ? FAST_SYSTEM_MS_PER_LINE
            : isPaystub ? PAYSTUB_MS_PER_LINE
            : SYSTEM_MS_PER_LINE;

          const bodyWrap = document.createElement("div");
          Object.assign(bodyWrap.style, {
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            color: "var(--cubicle-text)",
            fontSize: "12px",
            letterSpacing: "0",
          } as CSSStyleDeclaration);
          wrap.append(bodyWrap);

          for (const line of block.body) {
            await sleep(msPerLine);
            const p = document.createElement("div");
            p.textContent = line;
            bodyWrap.append(p);
            log.scrollTop = log.scrollHeight;
          }
        }

        if (block.buttons && block.buttons.length > 0) {
          const buttonRow = document.createElement("div");
          Object.assign(buttonRow.style, {
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            marginTop: "4px",
          } as CSSStyleDeclaration);
          for (const btn of block.buttons) {
            const b = document.createElement("button");
            b.type = "button";
            b.textContent = btn.label;
            b.disabled = true;
            Object.assign(b.style, {
              background: "color-mix(in srgb, var(--cubicle-amber) 8%, transparent)",
              border: "1px solid var(--cubicle-bezel-edge)",
              color: "var(--cubicle-amber)",
              padding: "6px 12px",
              fontFamily: "\"Nunito\", \"Avenir Next\", system-ui, sans-serif",
              fontSize: "10px",
              letterSpacing: "0.10em",
              cursor: "pointer",
              borderRadius: "2px",
            } as CSSStyleDeclaration);
            b.addEventListener("click", () => {
              if (b.disabled) return;
              for (const sib of buttonRow.querySelectorAll("button")) {
                (sib as HTMLButtonElement).disabled = true;
              }
              btn.onClick();
            });
            buttonRow.append(b);
          }
          wrap.append(buttonRow);
          for (const b of buttonRow.querySelectorAll("button")) {
            (b as HTMLButtonElement).disabled = false;
          }
        }

        renderedEl = wrap;
      },
    };

    const handle = {
      collapse() {
        if (renderedEl) {
          renderedEl.remove();
        } else {
          job.cancelled = true;
          const idx = queue.indexOf(job);
          if (idx >= 0) queue.splice(idx, 1);
        }
      },
    };

    enqueue(job);
    return handle;
  }

  // ─── Submit wiring — single handler, replace semantics ──────────
  let submitHandler: ((text: string) => void) | null = null;
  let inputEnabled = true;
  inputRow.addEventListener("submit", (e) => {
    e.preventDefault();
    if (!inputEnabled) return;
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    if (submitHandler) submitHandler(text);
  });

  return {
    appendQT,
    appendSystem,
    appendPilot(text) {
      const job: Job = {
        kind: "pilot",
        cancelled: false,
        async run() { appendPilotRow(text); },
      };
      enqueue(job);
    },
    onPilotSubmit(handler) { submitHandler = handler; },
    setInputEnabled(enabled) {
      inputEnabled = enabled;
      input.disabled = !enabled;
    },
    setSpeakerLabel(label) { speakerLabel = label; },
    setGrayedOut(grayed) {
      if (grayed) {
        root.style.opacity = "0.4";
        root.style.filter = "grayscale(1)";
        root.style.pointerEvents = "none";
      } else {
        root.style.opacity = "";
        root.style.filter = "";
        root.style.pointerEvents = "";
      }
    },
    blurInput() { input.blur(); },
    focusInput() { input.focus(); },
  };
}
